import React from "react";
import { ASSETS } from "../config/assets";

interface ProConItem {
  text: string | React.ReactNode;
}

interface ProductSectionProps {
  rank?: number;
  title: string;
  imageUrl: string;
  effectiveness: string;
  safety: string;
  price: string;
  overall: string;
  starRating?: string;
  description: (string | React.ReactNode)[];
  quote?: {
    text: string | React.ReactNode;
    author: string;
  };
  pros?: ProConItem[];
  cons?: ProConItem[];
  ctaUrl?: string;
  ctaText?: string;
  showVisitWebsite?: boolean;
  additionalContent?: React.ReactNode;
  effectiveness_label: string;
  safety_label: string;
  price_label: string;
  overall_label: string;
  pros_label: string;
  cons_label: string;
  cta_label: string;
}

const ProductSection: React.FC<ProductSectionProps> = ({
  rank,
  title,
  imageUrl,
  effectiveness,
  safety,
  price,
  overall,
  starRating = "★★★★★",
  description,
  quote,
  pros = [],
  cons = [],
  ctaUrl,
  showVisitWebsite = true,
  additionalContent,
  effectiveness_label,
  safety_label,
  price_label,
  overall_label,
  pros_label,
  cons_label,
  cta_label,
}) => {
  const renderStars = (rating: string) => {
    const goldStars = rating.match(/★/g)?.length || 0;
    const totalStars = 5;
    const whiteStars = totalStars - goldStars;

    return (
      <span className="star-gold">
        {"★".repeat(goldStars)}
        {whiteStars > 0 && (
          <span className="star-white">{"★".repeat(whiteStars)}</span>
        )}
      </span>
    );
  };

  // 1. Bulletproof filter: Removes nulls, undefined, and empty spaces ("   ")
  const validPros = (pros || []).filter((item) => {
    if (!item || item.text == null) return false;
    if (typeof item.text === "string" && item.text.trim() === "") return false;
    return true;
  });

  const validCons = (cons || []).filter((item) => {
    if (!item || item.text == null) return false;
    if (typeof item.text === "string" && item.text.trim() === "") return false;
    return true;
  });

  return (
    <div className="text">
      {ctaUrl ? (
        <a href={ctaUrl}>
          <h2 className="heading_2">
            {rank && `${rank}. `}
            {title}
          </h2>
        </a>
      ) : (
        <h2 className="heading_2">
          {rank && `${rank}. `}
          {title}
        </h2>
      )}

      {ctaUrl ? (
        <a href={ctaUrl}>
          <img src={imageUrl} alt={title} className="center" />
        </a>
      ) : (
        <img src={imageUrl} alt={title} className="center" />
      )}

      <p>
        <b>{effectiveness_label}:</b> {effectiveness}
      </p>
      <p>
        <b>{safety_label}:</b> {safety}
      </p>
      <p>
        <b>{price_label}:</b> {price}
      </p>
      <p>
        <b>{overall_label}:</b> {overall} {renderStars(starRating)}
      </p>

      {description.map((paragraph, index) => (
        <div
          key={index}
          style={{ fontFamily: "Helvetica Neue, Helvetica, Arial, sans-serif" }}
        >
          <p>{paragraph}</p>
        </div>
      ))}

      {quote && (
        <div className="quote">
          "{quote.text}" - <b>{quote.author}</b>
        </div>
      )}

      {/* 2. Strict rendering using ? : null so it physically cannot render an empty shell */}
      {validPros.length > 0 || validCons.length > 0 ? (
        <>
          {validPros.length > 0 ? (
            <div className="inner-box green">
              <h1>{pros_label}</h1>
              <ul>
                {validPros.map((item, index) => (
                  <li key={index}>
                    <img
                      src={ASSETS.icons.checkmark}
                      className="check-logo"
                      alt=""
                    />
                    <span>{item.text}</span>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          {validCons.length > 0 ? (
            <div className="inner-box red">
              <h1>{cons_label}</h1>
              <ul>
                {validCons.map((item, index) => (
                  <li key={index}>
                    <img
                      src={ASSETS.icons.cross}
                      className="check-logo"
                      alt=""
                    />
                    <span>{item.text}</span>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </>
      ) : null}

      {showVisitWebsite && ctaUrl ? (
        <div className="save-box">
          <a href={ctaUrl}>{cta_label}</a>
        </div>
      ) : null}

      {additionalContent}
    </div>
  );
};

export default ProductSection;
